import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Guards a single-image selection against its own async picks.
 *
 * Picked images are downscaled before they are selected, which can take a
 * noticeable fraction of a second for a large photo, so picks overlap: an
 * earlier pick can land after a newer one, or after the selection was cleared
 * or its component unmounted. `begin()` starts a pick and returns a check that
 * stays true only while nothing has superseded it; `cancel()` supersedes every
 * pick in flight.
 */
export function useLatestPick(): {
  begin: () => () => boolean;
  cancel: () => void;
} {
  const latest = useRef(0);

  const cancel = useCallback(() => {
    latest.current++;
  }, []);
  useEffect(() => cancel, [cancel]);

  const begin = useCallback(() => {
    const pick = ++latest.current;
    return () => pick === latest.current;
  }, []);

  return useMemo(() => ({ begin, cancel }), [begin, cancel]);
}
